import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Form, Button, Card, Alert } from 'react-bootstrap';
import { ArrowUpRight, Eye, EyeSlash } from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/AuthContext';
import AuthCardLeft from './AuthCardLeft';
import MobileDesktopOnlyModal from './MobileDesktopOnlyModal';
import './Auth.css';

const SignIn: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signInWith2FA } = useAuth();

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setEmailError('');
    setPasswordError('');

    // Custom validation
    let hasErrors = false;
    if (!email) {
      setEmailError('Email is required');
      hasErrors = true;
    } else {
      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setEmailError('Please enter a valid email address');
        hasErrors = true;
      }
    }
    if (!password) {
      setPasswordError('Password is required');
      hasErrors = true;
    }

    if (hasErrors) return;

    setLoading(true);

    try {
      await signIn(email, password, rememberMe);
      navigate('/home');
    } catch (error: any) {
      if (error.message === '2FA_REQUIRED') {
        setRequires2FA(true);
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
        setError('Incorrect email or password');
      } else if (error.code === 'auth/wrong-password') {
        setPasswordError('Incorrect password');
      } else if (error.code === 'auth/invalid-email') {
        setEmailError('Invalid email address');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later');
      } else {
        setError('Failed to sign in. Please try again');
        console.error('Sign in error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setCodeError('');

    // Custom validation
    if (!verificationCode || verificationCode.length !== 6) {
      setCodeError('Please enter a 6-digit verification code');
      return;
    }

    setLoading(true);

    try {
      await signInWith2FA(verificationCode);
      navigate('/home');
    } catch (error: any) {
      if (error.code === 'auth/invalid-verification-code') {
        setCodeError('Invalid verification code');
      } else if (error.code === 'auth/code-expired') {
        setCodeError('Verification code has expired. Please sign in again');
        setTimeout(() => setRequires2FA(false), 2000);
      } else {
        setCodeError('Failed to verify code. Please try again');
        console.error('2FA error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <MobileDesktopOnlyModal />
      <Card className="auth-card">
        <Card.Body>
          <AuthCardLeft />
          <div className="auth-card-right">
            <div className="auth-card-right-content">
              {!requires2FA && (
                <>
                  <h1 className="text-center">Sign in to your account</h1>
                  <h2 className="text-center mb-4">Welcome back! Please enter your details.</h2>
                </>
              )}

              {successMessage && <Alert variant="success">{successMessage}</Alert>}
              {error && <div className="text-danger text-center mb-3">{error}</div>}

              {!requires2FA ? (
              <Form onSubmit={handleSubmit} noValidate>
                <Form.Group id="email" className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError('');
                    }}
                    disabled={loading}
                    placeholder="name@example.com"
                    isInvalid={!!emailError}
                  />
                  {emailError && <Form.Text className="text-danger">{emailError}</Form.Text>}
                </Form.Group>

                <Form.Group id="password" className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <div className="password-field-container">
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError('');
                      }}
                      disabled={loading}
                      placeholder="Enter your password"
                      isInvalid={!!passwordError}
                      className="password-field-padded"
                    />
                    <Button
                      variant="link"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                      className="password-toggle-btn"
                    >
                      {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </Button>
                  </div>
                  {passwordError && <Form.Text className="text-danger">{passwordError}</Form.Text>}
                </Form.Group>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Form.Group className="mb-0">
                    <Form.Check
                      type="checkbox"
                      id="rememberMe"
                      label="Remember me"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={loading}
                    />
                  </Form.Group>
                  <Link to="/forgot-password">FORGOT PASSWORD</Link>
                </div>

                <Button
                  disabled={loading || !email || !password}
                  className="w-100"
                  type="submit"
                >
                  {loading ? 'SIGNING IN...' : 'SIGN IN'}
                </Button>
              </Form>
          ) : (
            <Form onSubmit={handle2FASubmit} noValidate>
              <Form.Group id="verificationCode" className="mb-3">
                <Form.Label>Verification Code</Form.Label>
                <Form.Control
                  type="text"
                  value={verificationCode}
                  onChange={(e) => {
                    setVerificationCode(e.target.value);
                    setCodeError('');
                  }}
                  disabled={loading}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  isInvalid={!!codeError}
                />
                {!codeError && (
                  <Form.Text className="text-muted">
                    Enter the verification code sent to your phone
                  </Form.Text>
                )}
                {codeError && <Form.Text className="text-danger">{codeError}</Form.Text>}
              </Form.Group>
              
              <Button 
                disabled={loading} 
                className="w-100" 
                type="submit"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </Button>
              
              <Button
                variant="link"
                className="w-100 mt-2"
                onClick={() => {
                  setRequires2FA(false);
                  setVerificationCode('');
                  setError('');
                }}
              >
                Back to Sign In
              </Button>
            </Form>
          )}
              {!requires2FA && (
                <div className="w-100 text-center mt-2">
                  <h2>Don't have an account? &nbsp;<Link to="/register">REGISTER</Link> <ArrowUpRight className="me-2" /></h2>
                </div>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SignIn;